import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { isCurrentPathAnonymousBrandFlow } from '../config/brands.config'
import { isWhatsAppCheckoutPath } from '../utils/renderActions'
import { getGAClientId, getGASessionId } from '../utils/googleAnalytics'
import { getAccessToken, setAccessToken } from './authToken'

// Sinal explícito por requisição pro responseErrorInterceptor — necessário
// porque algumas rotas (ex. /mfa/enroll/*) são compartilhadas por 2
// contextos que precisam de tratamento DIFERENTE de sessão expirada (nota 3
// portal-cannect-2fa §4/§8): o fluxo de login (useTwoFactorLoginFlow, deve
// suprimir o redirect — ele já trata a resposta sozinho) e a tela
// autenticada de gestão de 2FA (TwoFactorSettings, sessão completa normal,
// onde expirar/revogar deve continuar redirecionando). Path sozinho não
// distingue os dois (achado do Lince, Gate D) — por isso o CALLER decide.
declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean
  }
}

const locationHost = window.location.host
export const baseURL =
  locationHost.includes('homolog') || locationHost.includes('localhost')
    ? import.meta.env.VITE_APP_HOMOL_BASE_URL
    : import.meta.env.VITE_APP_BASE_URL

// withCredentials: o refresh token viaja num cookie httpOnly restrito a
// /v1/auth — exige esta origem na ORIGIN_WHITELIST do backend.
const api = axios.create({ baseURL, withCredentials: true })

const REFRESH_URL = '/auth/refresh'

type RetriableRequestConfig = AxiosRequestConfig & { retried?: boolean }

/**
 * Compat: os fluxos de login/cadastro chamam isto com o token da resposta.
 * Hoje só alimenta o access em memória (nunca limpa — logout usa clearLocalSession).
 */
export const defaultsHeadersAxios = (token: string) => {
  if (token) setAccessToken(token)
}

export const clearLocalSession = () => {
  setAccessToken(null)
  // resíduo de sessões anteriores à migração para cookie httpOnly
  localStorage.removeItem('@CANNECT_TOKEN_API')
}

let refreshRequest: Promise<string> | null = null

/**
 * Single-flight: N requests que tomam 401 juntas dividem um único
 * POST /auth/refresh, e o backend rotaciona o cookie uma vez só.
 */
export const refreshSession = (): Promise<string> => {
  if (!refreshRequest) {
    refreshRequest = api
      .post(REFRESH_URL)
      .then(({ data }) => {
        setAccessToken(data.token)
        return data.token as string
      })
      .finally(() => {
        refreshRequest = null
      })
  }

  return refreshRequest
}

/** Revoga a sessão no servidor (família do refresh) e limpa o cookie httpOnly. */
export const logoutServerSession = () => api.post('/auth/logout').catch(() => undefined)

api.interceptors.request.use(
  async (config) => {
    const accessToken = getAccessToken()

    if (accessToken) {
      if (!config.headers) {
        // eslint-disable-next-line no-param-reassign
        config.headers = {} as any
      }
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    const storedParams = sessionStorage.getItem('utmParams')
    let utmParams: Record<string, string> = {}

    if (storedParams) {
      utmParams = JSON.parse(storedParams) as Record<string, string>
    }

    // Em paralelo e com catch proprio: analytics nunca pode bloquear ou
    // derrubar uma requisicao de negocio.
    const [gaClientId, gaSessionId] = await Promise.all([
      getGAClientId().catch(() => null),
      getGASessionId().catch(() => null)
    ])

    if (!config.headers) {
      config.headers = {} as any
    }

    if (gaClientId) {
      config.headers['x-ga-client-id'] = gaClientId
    }
    if (gaSessionId) {
      config.headers['x-ga-session-id'] = gaSessionId
    }

    if (config.method === 'post' || config.method === 'put') {
      if (config.data instanceof FormData) {
        Object.keys(utmParams).forEach((key) => {
          config.data.append(key, utmParams[key])
        })

        if (gaClientId) config.data.append('ga_client_id', gaClientId)
        if (gaSessionId) config.data.append('ga_session_id', gaSessionId)
      } else {
        config.data = {
          ...config.data,
          utmParams,
          ga_client_id: gaClientId,
          ga_session_id: gaSessionId
        }
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const responseSuccessInterceptor = (response: AxiosResponse) => response

// Nota 3 (portal-cannect-2fa) §1/§2/§3 — estas rotas SÓ são chamadas pelo
// fluxo de login (usecases/user.ts:authenticateWithChallenge,
// usecases/mfa.ts:verifyLoginChallenge/resendLoginChallenge, confirmado por
// busca global — nenhuma tela autenticada as chama), que já lê o 401/403
// delas no próprio .catch pra decidir o passo certo
// (challenge/method_required/enrollment_required). Se o interceptor global
// tratasse essa mesma resposta como sessão expirada primeiro, ele
// redirecionaria pra /login de forma SÍNCRONA antes do .catch do chamador
// rodar (bug real, corrigido: /auth/portal/login devolvia 403
// MFA_ENROLLMENT_REQUIRED com token, mas a tela voltava pro login vazio em
// vez de abrir o modal de enrollment). `/authenticate` continua na lista por
// segurança (nenhum chamador do frontend usa mais, mas é o mesmo tipo de
// rota de negócio). `/mfa/enroll/*` NÃO entra aqui por
// path — é rota compartilhada com TwoFactorSettings (tela autenticada), ver
// `skipAuthRedirect` acima. Mesmo motivo pelo qual 401 nestas rotas nunca
// tenta refresh silencioso: é resposta de negócio (credencial errada) ou
// ausência de sessão no bootstrap — renovar aqui seria loop.
const LOGIN_FLOW_EXACT_PATHS = new Set([
  REFRESH_URL,
  '/auth/logout',
  '/auth/portal/login',
  '/authenticate',
  '/authenticate/mfa/verify',
  '/authenticate/mfa/resend',
  '/authenticate-social',
  '/verify-token',
  '/forgot_password',
  '/reset_password',
  '/reset-password'
])
const isLoginFlowRequest = (path: string) => LOGIN_FLOW_EXACT_PATHS.has(path)

const responseErrorInterceptor = async (error: AxiosError<{ code?: string }>) => {
  const responseStatus = error.response?.status
  const responseCode = error.response?.data?.code
  const requestPath = error.config?.url || ''
  const skipAuthRedirect = error.config?.skipAuthRedirect

  // Nota 3 (portal-cannect-2fa) §4/§8: um token emitido com login_context
  // 'checkout' (mfa:false) completa a compra, mas qualquer chamada FORA da
  // allowlist de checkout passa a devolver MFA_REQUIRED/MFA_ENROLLMENT_REQUIRED
  // (401/403) — não é sessão inválida.
  const isMfaGateCode = responseCode === 'MFA_REQUIRED' || responseCode === 'MFA_ENROLLMENT_REQUIRED'
  const isSessionGateStatus = responseStatus === 401 || (responseStatus === 403 && isMfaGateCode)

  if (!isSessionGateStatus) return Promise.reject(error)

  const originalRequest = error.config as RetriableRequestConfig | undefined
  const isHandledByLoginFlow = isLoginFlowRequest(requestPath) || skipAuthRedirect

  // Rota de login/refresh/logout ou chamador que já trata a resposta sozinho
  // (ex.: challenge de 2FA) — não redireciona nem tenta renovar.
  if (isHandledByLoginFlow) {
    return Promise.reject(error)
  }

  // Access expirado (401 puro, fora do gate de MFA): tenta renovar pelo
  // cookie httpOnly uma única vez e repete a request original com o token
  // novo antes de derrubar a sessão.
  if (responseStatus === 401 && originalRequest && !originalRequest.retried) {
    originalRequest.retried = true

    try {
      await refreshSession()

      return await api(originalRequest)
    } catch {
      // sem sessão renovável: cai para o logout abaixo
    }
  }

  const lowerPath = (window.location.pathname || '').toLowerCase()
  const isPublicFlow =
    lowerPath.startsWith('/detalhes-do-pedido') ||
    lowerPath.startsWith('/nps') ||
    lowerPath.startsWith('/validacao-documentos') ||
    isWhatsAppCheckoutPath(lowerPath) ||
    // Whitelabel sem tela de login (Remederi/Vikura): um 401 aqui não pode
    // redirecionar — mandar para /login da Cannect quebra o funil da marca.
    isCurrentPathAnonymousBrandFlow()
  const isAspaecFlow = lowerPath.startsWith('/aspaec') || lowerPath.startsWith('/areaassociados')

  // Sessão leve (mfa:false) continua renovável pelo cookie: sem revogar, o /auth/refresh do /login a recria e o gate redireciona de novo, em loop.
  if (isMfaGateCode && !isPublicFlow) await logoutServerSession()

  clearLocalSession()
  if (!isPublicFlow) {
    const loginPath = isAspaecFlow ? '/AreaAssociados' : '/login'
    window.location.replace(`${window.location.origin}${loginPath}`)
  }

  return Promise.reject(error)
}

api.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor)

export default api
